<template>
  <div class="wrap">

    <div class="relative date-picker-wrap point mb-[8px]">
      <img src="../../assets/image/icon/space/calendar_month.svg"
           alt="">
      <div id="datepicker">{{ startDateShow }}</div>
      <div class="absolute left-0 top-0 right-0 bottom-0" @click="handleDateClick"></div>
    </div>

    <div class="date-picker-wrap point mb-[20px]">
      <img v-if="rentSetting?.length > 0"
           src="../../assets/image/icon/space/pace.svg"
           alt="">
      <select v-if="rentSetting?.length > 0"
              class="point"
              v-model="selectedPeriod"
              @change=onTimeSelected>
        <option disabled
                value="">{{ $t('space.period_placeholder') }}
        </option>
        <option v-for="period in rentSetting"
                :value="period">
          {{
            `${period.start.slice(0, 5)} ～ ${period.end.slice(0, 5)}  NT$${parseNumber(period.price)}`
          }}
        </option>
      </select>
      <div v-else
           class="empty">{{ $t('space.today_space_empty') }}
      </div>
    </div>

  </div>
</template>
<script setup>
import {computed, onMounted, ref} from "vue";
import {DateTime, easepick, LockPlugin} from '@easepick/bundle';
import {DateFormat} from "@/utils/dateFormat.js";
import {getSpaceUsageStatus} from "@/api/space";
import {convertTo24HourFormat, parseNumber} from "@/utils/parseTool.js";
import {isDateLocked, tuneCrossDay} from "~/utils/newPriceTool.ts";
import {canRentMaxDay} from "@/config/common.js";

const picker = ref(null)
const startDate = ref('')
const startDateShow = ref('')
const rentSetting = ref([])
const selectedPeriod = ref('')


const props = defineProps({
  spaceId: {
    type: Number,
    default: -1
  },
  advanceBookingDays: {
    type: Number,
    default: 0
  },
  spaceRent: {
    type: Object,
    default: undefined
  },
  spaceUsageStatus: {
    type: Array,
    default: []
  }
})
const lockDate = computed(() => {
  return props.spaceUsageStatus
      .filter(d => d.begin_time === '00:00:00' && d.end_time === '23:59:59')
      .map(d => new DateTime(d.date, 'YYYY/MM/DD'))
})

const emit = defineEmits(['onTimeSelected'])

function onTimeSelected() {
  const isOK = startDate.value !== '' && selectedPeriod.value !== ''
  const rent_type = 4
  let begin_time = `${startDate.value} ${selectedPeriod.value.start}:00`
  let end_time = `${startDate.value} ${selectedPeriod.value.end}:00`

  /// 跨日
  if (parseInt(selectedPeriod.value.start?.slice(0, 2)) >= 24) {
    begin_time = `${addOneDay(startDate.value)} ${convertTo24HourFormat(selectedPeriod.value.start)}:00`
  }
  if (parseInt(selectedPeriod.value.end?.slice(0, 2)) >= 24) {
    end_time = `${addOneDay(startDate.value)} ${convertTo24HourFormat(selectedPeriod.value.end)}:00`
  }
  emit('onTimeSelected', isOK, rent_type, begin_time, end_time, selectedPeriod.value.price)
}

watch(() => props.spaceRent, () => {
  updatePicker()
})

function updatePicker() {
  picker.value?.destroy()
  initDatePicker()
}

onMounted(() => {
  initDatePicker()
})

function initDatePicker() {
  const todayDate = new Date();
  const maxDate = new Date().setDate(todayDate.getDate() + canRentMaxDay);

  todayDate.setDate(todayDate.getDate() + props.advanceBookingDays)

  const defaultDay = new Date()
  defaultDay.setDate(defaultDay.getDate() + props.advanceBookingDays)

  /////////////////////////////////////////////////////////////////////
  /// 避開已預定日期邏輯 start
  /////////////////////////////////////////////////////////////////////
  /// 原先避開已預定日期邏輯
  // for (let i = 0; i < props.spaceUsageStatus.length; i++) {
  //   if (props.spaceUsageStatus[i].date === DateFormat.format.date(defaultDay, 'yyyy/MM/dd')) {
  //     defaultDay.setDate(defaultDay.getDate() + 1)
  //   }
  // }
  /// 新 避開已預定日期邏輯
  const isLocked = (date) => {
    const formattedDate = DateFormat.format.date(date, 'yyyy/MM/dd');
    return (
        date.inArray(lockDate.value, '[]') ||
        isDateLocked(props.spaceRent, formattedDate) ||
        props.spaceUsageStatus.some((status) => status.date === formattedDate)
    )
  }
  let count = 0
  while (isLocked(new DateTime(defaultDay, 'YYYY/MM/DD')) && count < canRentMaxDay) {
    defaultDay.setDate(defaultDay.getDate() + 1)
    count++
  }
  /////////////////////////////////////////////////////////////////////
  /// 避開已預定日期邏輯 end
  /////////////////////////////////////////////////////////////////////

  let option = {
    element: document.getElementById('datepicker'),
    plugins: [LockPlugin],
    css: [
      'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css'
    ],
    zIndex: 99,
    lang: 'zh-TW',
    date: defaultDay,
    LockPlugin: {
      minDate: todayDate,
      maxDate: maxDate,
      inseparable: true,
      filter(date, picked) {
        return date.inArray(lockDate.value, '[]') || isDateLocked(props.spaceRent, DateFormat.format.date(date, 'yyyy/MM/dd'))
      }
    },
    setup(picker) {
      picker.on('select', selectDate)
    },
  }

  picker.value = new easepick.create(option);

  selectDate({detail: {date: defaultDay}})
}

function selectDate(event) {
  startDateShow.value = 'yyyy/MM/dd'

  nextTick(() => {
    startDate.value = DateFormat.format.date(event.detail.date, 'yyyy/MM/dd')
    startDateShow.value = DateFormat.format.date(event.detail.date, 'yyyy年MM月dd日 (E)')
    handleSpaceInfo(event.detail.date)
  })
}

function handleDateClick() {
  document.getElementById('datepicker').click();
}

async function handleSpaceInfo(date) {
  selectedPeriod.value = ''
  onTimeSelected()

  const pickerDate = DateFormat.format.date(date, 'yyyy/MM/dd')

  try {
    const res = await getSpaceUsageStatus(parseInt(props.spaceId), pickerDate)
    const space_usage_status = res.space_usage_status
    /// 先拿全部選項
    rentSetting.value = getRentSettingByDate(props.spaceRent, pickerDate)

    const now = new Date()
    for (let i = 0; i < rentSetting.value.length; i++) {
      const period_detail = rentSetting.value[i]
      /// 刪除時段小於現在時間
      const period_start = new Date(DateFormat.format.date(date, 'yyyy/MM/dd') + " " + period_detail.start).getTime();
      if (now.getTime() > period_start) {
        rentSetting.value.splice(rentSetting.value.indexOf(period_detail), 1)
        i--
      }
      /// 刪除時段重疊選項
      else if (space_usage_status.some(d => {
        const period_start = new Date(d.date + " " + period_detail.start).getTime()
        const period_end = new Date(d.date + " " + period_detail.end).getTime()
        const usage_start = new Date(d.date + " " + d.begin_time).getTime()
        const usage_end = new Date(d.date + " " + d.end_time).getTime()
        return period_start < usage_end && period_end > usage_start
      })) {
        rentSetting.value.splice(rentSetting.value.indexOf(period_detail), 1)
        i--
      }
    }
  } catch (error) {
    console.log(error)
  }
}
</script>
<style scoped
       lang="scss">
.wrap {
  @apply pt-[16px] relative;

  .date-picker-wrap {
    @apply flex items-center justify-center w-full h-[50px] text-[18px] text-osn-dark-grey-00 font-bold rounded-[50px];
    border: 1px solid var(--osn-grey-03, #E1E0E0);

    img {
      @apply w-[20px] mr-[6px];
    }

    select {
      @apply text-center;
      appearance: none;
      -moz-appearance: none;
      -webkit-appearance: none;
      border: none;
      background: none;
      outline: none;
    }

    .empty {
      @apply text-red-600;
    }
  }

}
</style>
